import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  formatMfaRequiredDate,
  getAdminMfaPhase,
  type AdminMfaPhase,
} from '../../config/mfaPolicy';
import { isPrivilegedAdminRole, listVerifiedTotpFactors } from '../../services/auth/mfaService';
import { profileService } from '../../services/users/profileService';

export const MFA_GRACE_BANNER_DISMISSED_KEY = 'mfa-grace-banner-dismissed';

export function isMfaGraceBannerSessionDismissed(): boolean {
  try {
    return sessionStorage.getItem(MFA_GRACE_BANNER_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function dismissMfaGraceBannerForSession(): void {
  try {
    sessionStorage.setItem(MFA_GRACE_BANNER_DISMISSED_KEY, 'true');
  } catch {
    // sessionStorage may be unavailable in restricted contexts
  }
}

export function shouldShowMfaGraceBanner(options: {
  role: string | null | undefined;
  phase: AdminMfaPhase;
  factorCount: number;
  dismissed: boolean;
}): boolean {
  if (options.dismissed) return false;
  if (!isPrivilegedAdminRole(options.role)) return false;
  if (options.phase !== 'grace') return false;
  if (options.factorCount > 0) return false;
  return true;
}

const MfaGraceBanner: React.FC = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => isMfaGraceBannerSessionDismissed());

  useEffect(() => {
    let cancelled = false;

    const evaluate = async () => {
      if (!user?.id || dismissed) {
        if (!cancelled) setVisible(false);
        return;
      }

      const phase = getAdminMfaPhase();
      if (phase !== 'grace') {
        if (!cancelled) setVisible(false);
        return;
      }

      try {
        const [profile, factors] = await Promise.all([
          profileService.getProfile(user.id),
          listVerifiedTotpFactors(),
        ]);

        if (cancelled) return;

        setVisible(
          shouldShowMfaGraceBanner({
            role: profile?.user_role,
            phase,
            factorCount: factors.length,
            dismissed,
          })
        );
      } catch {
        if (!cancelled) setVisible(false);
      }
    };

    void evaluate();

    return () => {
      cancelled = true;
    };
  }, [user?.id, dismissed]);

  const handleDismiss = () => {
    dismissMfaGraceBannerForSession();
    setDismissed(true);
    setVisible(false);
  };

  if (!visible) return null;

  const requiredDate = formatMfaRequiredDate();

  return (
    <div
      role="alert"
      className="flex flex-col gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
    >
      <div className="flex min-w-0 items-start gap-3 text-sm text-amber-950">
        <Shield className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
        <p>
          Multi-factor authentication becomes mandatory on {requiredDate}.{' '}
          <Link
            to="/mfa/enroll"
            className="font-medium text-amber-900 underline hover:text-amber-950"
          >
            Set it up now.
          </Link>
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss MFA reminder"
        className="self-end rounded p-1 text-amber-800 hover:bg-amber-100 sm:self-center"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default MfaGraceBanner;
